import type {
  AdvanceAnnouncementAction,
  CardPosition,
  ConfirmRolePeekAction,
  GameState,
  NightRoleStep,
  RoleCard,
  StartNightAction,
  SubmitNightActionAction,
} from "../types";

function nextPendingNightStepIndex(state: GameState): number {
  return state.nightRoleQueue.findIndex((step) => step.status === "pending");
}

function nextPendingAnnouncementIndex(state: GameState): number {
  return state.announcerTimeline.findIndex((step) => step.status === "pending");
}

function isNightComplete(state: GameState): boolean {
  return (
    state.nightRoleQueue.every((step) => step.status === "complete") &&
    state.announcerTimeline.every((step) => step.status === "complete")
  );
}

function withNightPhase(state: GameState): GameState {
  if (isNightComplete(state)) {
    return {
      ...state,
      phase: "day",
      discussionRemainingSeconds: state.discussionDurationSeconds,
      voteRemainingSeconds: null,
      autoVotePlayerIds: [],
      players: state.players.map((player) => ({
        ...player,
        voteTargetId: null,
      })),
    };
  }
  return { ...state, phase: "night" };
}

function cardById(state: GameState, cardId: string): RoleCard {
  const card = state.cards.find((candidate) => candidate.id === cardId);
  if (!card) {
    throw new Error(`Unknown card id: ${cardId}`);
  }
  return card;
}

function findCardIdAtPlayer(state: GameState, playerId: string): string {
  const entry = Object.entries(state.cardPositions).find(
    ([, position]) =>
      position.zone === "player" && position.playerId === playerId,
  );
  if (!entry) {
    throw new Error(`No card at player: ${playerId}`);
  }
  return entry[0];
}

function cardIdAtCenter(state: GameState, slotIndex: 0 | 1 | 2): string {
  const cardId = state.centerSlots[slotIndex];
  if (!cardId) {
    throw new Error(`No card at center slot: ${slotIndex}`);
  }
  return cardId;
}

function addPrivateKnowledge(
  state: GameState,
  playerId: string,
  cardIds: readonly string[],
): GameState {
  if (cardIds.length === 0) return state;
  const previous = state.privateKnowledge[playerId] ?? [];
  const next = [...previous];
  for (const cardId of cardIds) {
    if (!next.includes(cardId)) {
      next.push(cardId);
    }
  }
  return {
    ...state,
    privateKnowledge: {
      ...state.privateKnowledge,
      [playerId]: next,
    },
  };
}

function swapPlayerCards(
  state: GameState,
  leftPlayerId: string,
  rightPlayerId: string,
): GameState {
  const leftCardId = findCardIdAtPlayer(state, leftPlayerId);
  const rightCardId = findCardIdAtPlayer(state, rightPlayerId);
  const cardPositions: Record<string, CardPosition> = {
    ...state.cardPositions,
    [leftCardId]: { zone: "player", playerId: rightPlayerId },
    [rightCardId]: { zone: "player", playerId: leftPlayerId },
  };

  return { ...state, cardPositions };
}

function swapPlayerCardWithCenter(
  state: GameState,
  playerId: string,
  slotIndex: 0 | 1 | 2,
): GameState {
  const playerCardId = findCardIdAtPlayer(state, playerId);
  const centerCardId = cardIdAtCenter(state, slotIndex);
  const cardPositions: Record<string, CardPosition> = {
    ...state.cardPositions,
    [playerCardId]: { zone: "center", slotIndex },
    [centerCardId]: { zone: "player", playerId },
  };
  const centerSlots: GameState["centerSlots"] = [
    slotIndex === 0 ? playerCardId : state.centerSlots[0],
    slotIndex === 1 ? playerCardId : state.centerSlots[1],
    slotIndex === 2 ? playerCardId : state.centerSlots[2],
  ];

  return { ...state, cardPositions, centerSlots };
}

function applyPayload(
  state: GameState,
  action: SubmitNightActionAction,
): GameState {
  const payload = action.payload;

  switch (payload.kind) {
    case "werewolfPeekCenter":
      return addPrivateKnowledge(state, action.actorPlayerId, [
        cardIdAtCenter(state, payload.centerSlotIndex),
      ]);
    case "seerInspectPlayer":
      return addPrivateKnowledge(state, action.actorPlayerId, [
        findCardIdAtPlayer(state, payload.targetPlayerId),
      ]);
    case "seerInspectCenter":
      return addPrivateKnowledge(
        state,
        action.actorPlayerId,
        payload.centerSlotIndexes.map((slotIndex) =>
          cardIdAtCenter(state, slotIndex),
        ),
      );
    case "robberSwap": {
      const targetCardId = findCardIdAtPlayer(state, payload.targetPlayerId);
      const swapped = swapPlayerCards(
        state,
        action.actorPlayerId,
        payload.targetPlayerId,
      );
      return addPrivateKnowledge(swapped, action.actorPlayerId, [targetCardId]);
    }
    case "troublemakerSwap": {
      const [leftPlayerId, rightPlayerId] = payload.targetPlayerIds;
      return swapPlayerCards(state, leftPlayerId, rightPlayerId);
    }
    case "drunkSwapCenter":
      return swapPlayerCardWithCenter(
        state,
        action.actorPlayerId,
        payload.centerSlotIndex,
      );
    case "insomniacInspectOwn":
      return addPrivateKnowledge(state, action.actorPlayerId, [
        findCardIdAtPlayer(state, action.actorPlayerId),
      ]);
    case "doppelgangerCopy":
      return addPrivateKnowledge(state, action.actorPlayerId, [
        findCardIdAtPlayer(state, payload.targetPlayerId),
      ]);
    case "minionSeeWerewolves":
      return addPrivateKnowledge(
        state,
        action.actorPlayerId,
        startingCardIdsOfRoleActors(state, "werewolf", action.actorPlayerId),
      );
    case "masonSeeMasons":
      return addPrivateKnowledge(
        state,
        action.actorPlayerId,
        startingCardIdsOfRoleActors(state, "mason", action.actorPlayerId),
      );
    case "confirmNoAction":
      return state;
  }
}

function startingCardIdsOfRoleActors(
  state: GameState,
  roleId: GameState["nightRoleQueue"][number]["roleId"],
  excludePlayerId: string,
): readonly string[] {
  const step = state.nightRoleQueue.find(
    (candidate) => candidate.roleId === roleId,
  );
  const actorPlayerIds = step?.actorPlayerIds ?? [];
  return actorPlayerIds.flatMap((actorPlayerId) => {
    if (actorPlayerId === excludePlayerId) return [];
    const player = state.players.find(
      (candidate) => candidate.id === actorPlayerId,
    );
    return player?.startingCardId ? [player.startingCardId] : [];
  });
}

function grantWerewolfAllyKnowledge(
  state: GameState,
  action: SubmitNightActionAction,
): GameState {
  if (action.roleId !== "werewolf") return state;
  const allyCardIds = startingCardIdsOfRoleActors(
    state,
    "werewolf",
    action.actorPlayerId,
  );
  if (allyCardIds.length === 0) return state;
  return addPrivateKnowledge(state, action.actorPlayerId, allyCardIds);
}

function completeActor(state: GameState, actorPlayerId: string): GameState {
  const stepIndex = nextPendingNightStepIndex(state);
  if (stepIndex < 0) return state;

  const nextQueue = state.nightRoleQueue.map((step, index): NightRoleStep => {
    if (index !== stepIndex) return step;
    const completedPlayerIds = step.completedPlayerIds.includes(actorPlayerId)
      ? step.completedPlayerIds
      : [...step.completedPlayerIds, actorPlayerId];
    const status = step.actorPlayerIds.every((playerId) =>
      completedPlayerIds.includes(playerId),
    )
      ? "complete"
      : "pending";
    return {
      ...step,
      status,
      completedPlayerIds,
    };
  });

  return { ...state, nightRoleQueue: nextQueue };
}

function appendNightLog(
  state: GameState,
  action: SubmitNightActionAction,
): GameState {
  return {
    ...state,
    nightLog: [
      ...state.nightLog,
      `${action.actorPlayerId}:${action.roleId}:${action.payload.kind}`,
    ],
  };
}

function touch(state: GameState): GameState {
  return { ...state, version: state.version + 1 };
}

export function applyStartNight(
  state: GameState,
  _action: StartNightAction,
): GameState {
  return touch({ ...state, phase: "night" });
}

export function applyConfirmRolePeek(
  state: GameState,
  action: ConfirmRolePeekAction,
): GameState {
  const rolePeekConfirmedPlayerIds = state.rolePeekConfirmedPlayerIds.includes(
    action.actorPlayerId,
  )
    ? state.rolePeekConfirmedPlayerIds
    : [...state.rolePeekConfirmedPlayerIds, action.actorPlayerId];

  return touch({
    ...state,
    rolePeekConfirmedPlayerIds,
  });
}

export function applyAdvanceAnnouncement(
  state: GameState,
  action: AdvanceAnnouncementAction,
): GameState {
  const stepIndex = nextPendingAnnouncementIndex(state);
  const nextTimeline = state.announcerTimeline.map((step, index) => {
    if (index !== stepIndex || step.roleId !== action.roleId) return step;
    return { ...step, status: "complete" as const };
  });

  return withNightPhase(
    touch({
      ...state,
      announcerTimeline: nextTimeline,
    }),
  );
}

// Roles the doppelganger must act as IMMEDIATELY after copying, inside her
// own night step. The other wake roles are joined later in the queue instead.
const DOPPELGANGER_IMMEDIATE_ROLES: readonly string[] = [
  "seer",
  "robber",
  "troublemaker",
  "drunk",
];

const DOPPELGANGER_JOIN_ROLES: readonly string[] = [
  "werewolf",
  "mason",
  "minion",
  "insomniac",
];

function appendActorToRoleStep(
  state: GameState,
  roleId: GameState["nightRoleQueue"][number]["roleId"],
  actorPlayerId: string,
): GameState {
  return {
    ...state,
    nightRoleQueue: state.nightRoleQueue.map((step): NightRoleStep => {
      if (step.roleId !== roleId) return step;
      if (step.actorPlayerIds.includes(actorPlayerId)) return step;
      return {
        ...step,
        status: "pending",
        actorPlayerIds: [...step.actorPlayerIds, actorPlayerId],
      };
    }),
  };
}

function applyDoppelgangerCopy(
  state: GameState,
  action: SubmitNightActionAction,
): GameState {
  if (action.payload.kind !== "doppelgangerCopy") return state;
  const targetCardId = findCardIdAtPlayer(state, action.payload.targetPlayerId);
  const copiedRoleId = cardById(state, targetCardId).roleId;

  let next: GameState = {
    ...applyPayload(state, action),
    doppelgangerCopiedRoleId: copiedRoleId,
  };

  if (DOPPELGANGER_IMMEDIATE_ROLES.includes(copiedRoleId)) {
    // The doppelganger's step stays pending: she now performs the copied
    // role's action with a second submit.
    return withNightPhase(touch(appendNightLog(next, action)));
  }

  if (DOPPELGANGER_JOIN_ROLES.includes(copiedRoleId)) {
    next = appendActorToRoleStep(next, copiedRoleId, action.actorPlayerId);
  }

  next = completeActor(next, action.actorPlayerId);
  return withNightPhase(touch(appendNightLog(next, action)));
}

export function applySubmitNightAction(
  state: GameState,
  action: SubmitNightActionAction,
): GameState {
  cardById(state, findCardIdAtPlayer(state, action.actorPlayerId));

  if (action.payload.kind === "doppelgangerCopy") {
    return applyDoppelgangerCopy(state, action);
  }

  const withPayload = applyPayload(state, action);
  const withAllyKnowledge = grantWerewolfAllyKnowledge(withPayload, action);
  const withCompleteActor = completeActor(
    withAllyKnowledge,
    action.actorPlayerId,
  );
  const withLog = appendNightLog(withCompleteActor, action);

  return withNightPhase(touch(withLog));
}
